import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { ProjectItem } from '../types.ts';

/**
 * Generates an executive, publication-grade PDF dossier for a Project with detailed
 * work plan activities, visual Gantt timeline chart with progress bars, status summary, and signatures.
 */
export function exportProjectToPDF(project: ProjectItem) {
  const doc = new jsPDF({
    orientation: 'landscape',
    unit: 'mm',
    format: 'a4'
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  // Color constants
  const BRAND_DARK = [30, 41, 59] as const; // #1E293B
  const BRAND_RED = [229, 46, 46] as const;  // #E52E2E
  const BG_LIGHT = [248, 250, 252] as const;  // #F8FAFC
  const BORDER_COLOR = [226, 232, 240] as const; // #E2E8F0

  const drawHeader = (pageTitle: string) => {
    // Top navy bar
    doc.setFillColor(BRAND_DARK[0], BRAND_DARK[1], BRAND_DARK[2]);
    doc.rect(0, 0, pageWidth, 16, 'F');

    // Red accent line
    doc.setFillColor(BRAND_RED[0], BRAND_RED[1], BRAND_RED[2]);
    doc.rect(0, 16, pageWidth, 2.5, 'F');

    // Brand text
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('GRUPO ISN  |  SISTEMA INTEGRAL DE CONTROL OPERATIVO - SIC PRO', 14, 11);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.text(`${pageTitle}  •  ${project.code}`, pageWidth - 14, 11, { align: 'right' });
  };

  // ==========================================
  // PAGE 1: RESUMEN EJECUTIVO Y CRONOGRAMA GANTT VISUAL
  // ==========================================
  drawHeader('EXPEDIENTE EJECUTIVO & CRONOGRAMA GANTT');

  // Project Main Header
  doc.setTextColor(15, 23, 42);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.text(project.name, 14, 25);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(100, 116, 139);
  doc.text(`Unidad de Negocio: ${project.businessUnit}  |  Cliente: ${project.clientName}  |  Categoría: ${project.category}  |  Prioridad: ${project.priority}`, 14, 30);

  // --- EXECUTIVE METADATA & KPI CARDS (Y: 33 to 53) ---
  const totalTasks = project.tasks.length;
  const completedTasks = project.tasks.filter(t => t.status === 'COMPLETADA').length;
  const inProgressTasks = project.tasks.filter(t => t.status === 'EN_PROCESO').length;
  const delayedTasks = project.tasks.filter(t => t.status === 'RETRASADA' || t.status === 'BLOQUEADA').length;
  const pendingTasks = project.tasks.filter(t => t.status === 'POR_INICIAR' || t.status === 'EN_REVISION').length;

  // Metadata Card
  doc.setFillColor(BG_LIGHT[0], BG_LIGHT[1], BG_LIGHT[2]);
  doc.setDrawColor(BORDER_COLOR[0], BORDER_COLOR[1], BORDER_COLOR[2]);
  doc.roundedRect(14, 33, 175, 21, 2, 2, 'FD');

  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(71, 85, 105);

  doc.text('LÍDER / PM RESPONSABLE:', 18, 38);
  doc.text('VENTANA DE EJECUCIÓN:', 75, 38);
  doc.text('ESTADO GENERAL:', 130, 38);

  doc.setFont('helvetica', 'normal');
  doc.setTextColor(15, 23, 42);
  doc.text(project.manager || 'Líder Técnico', 18, 43);
  doc.text(`${project.startDate}  al  ${project.targetEndDate}`, 75, 43);

  const statusColor = project.status === 'COMPLETADO' || project.status === 'EN_TIEMPO' ? [5, 150, 105] :
                      project.status === 'EN_RIESGO' ? [217, 119, 6] : [220, 38, 38];
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(statusColor[0], statusColor[1], statusColor[2]);
  doc.text(project.status.replace('_', ' '), 130, 43);

  doc.setFont('helvetica', 'bold');
  doc.setTextColor(71, 85, 105);
  doc.text('OBJETIVO / ALCANCE:', 18, 49);
  doc.setFont('helvetica', 'italic');
  doc.setTextColor(51, 65, 85);
  const truncatedDesc = (project.description || '').length > 105 ? (project.description || '').substring(0, 105) + '...' : (project.description || 'Seguimiento técnico operativo continuo.');
  doc.text(truncatedDesc, 52, 49);

  // KPI Mini Cards on the right (Y: 33 to 53, X: 194 to 283)
  const kpiW = 20.5;
  const kpiX = 194;

  const kpis = [
    { title: 'TOTAL', val: `${totalTasks}`, sub: 'Actividades', color: [30, 41, 59] },
    { title: 'HECHAS', val: `${completedTasks}`, sub: `${totalTasks > 0 ? Math.round((completedTasks/totalTasks)*100) : 0}%`, color: [5, 150, 105] },
    { title: 'EN CURSO', val: `${inProgressTasks}`, sub: 'Ejecución', color: [2, 132, 199] },
    { title: 'DESVIACIÓN', val: `${delayedTasks}`, sub: 'Riesgo/Atraso', color: delayedTasks > 0 ? [220, 38, 38] : [100, 116, 139] }
  ];

  kpis.forEach((kpi, idx) => {
    const curX = kpiX + (idx * (kpiW + 2));
    doc.setFillColor(BG_LIGHT[0], BG_LIGHT[1], BG_LIGHT[2]);
    doc.setDrawColor(BORDER_COLOR[0], BORDER_COLOR[1], BORDER_COLOR[2]);
    doc.roundedRect(curX, 33, kpiW, 21, 1.5, 1.5, 'FD');

    doc.setFontSize(6.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(100, 116, 139);
    doc.text(kpi.title, curX + (kpiW / 2), 38, { align: 'center' });

    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(kpi.color[0], kpi.color[1], kpi.color[2]);
    doc.text(kpi.val, curX + (kpiW / 2), 44, { align: 'center' });

    doc.setFontSize(6);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(71, 85, 105);
    doc.text(kpi.sub, curX + (kpiW / 2), 49, { align: 'center' });
  });

  // Global Progress Bar strip (Y: 56 to 62)
  doc.setFillColor(241, 245, 249);
  doc.roundedRect(14, 56, pageWidth - 28, 6.5, 1.5, 1.5, 'F');
  
  const progFillW = Math.max(2, ((pageWidth - 28) * Math.min(100, Math.max(0, project.progressPercent))) / 100);
  doc.setFillColor(5, 150, 105);
  doc.roundedRect(14, 56, progFillW, 6.5, 1.5, 1.5, 'F');

  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(255, 255, 255);
  doc.text(`AVANCE GLOBAL PONDERADO: ${project.progressPercent}% COMPLETADO`, 18, 60.5);

  doc.setTextColor(30, 41, 59);
  doc.text(`Presupuesto: ${project.budgetTotal ? `$${project.budgetTotal.toLocaleString('es-MX')} ${project.budgetCurrency || 'MXN'}` : 'Sin asignar'}`, pageWidth - 18, 60.5, { align: 'right' });

  // --- VISUAL GANTT TIMELINE CHART (Y: 65 to 195) ---
  const ganttStartY = 65;
  const leftColWidth = 115; // WBS, Task Name, Assignee, Progress
  const timelineWidth = pageWidth - 28 - leftColWidth; // 269 - 115 = 154mm
  const timelineStartX = 14 + leftColWidth;

  // Timeline dates calculation
  const allDates: number[] = [];
  const startProj = new Date(project.startDate).getTime();
  const endProj = new Date(project.targetEndDate).getTime();
  if (!isNaN(startProj)) allDates.push(startProj);
  if (!isNaN(endProj)) allDates.push(endProj);

  project.tasks.forEach(t => {
    const s = new Date(t.startDate).getTime();
    const e = new Date(t.endDate).getTime();
    if (!isNaN(s)) allDates.push(s);
    if (!isNaN(e)) allDates.push(e);
  });

  const minTime = allDates.length > 0 ? Math.min(...allDates) : Date.now();
  const maxTime = allDates.length > 0 ? Math.max(...allDates) : Date.now() + 30 * 86400000;
  const totalDurationMs = Math.max(86400000 * 7, maxTime - minTime);

  // Divide timeline into 8 columns (Weeks/Intervals)
  const numTimeCols = 8;
  const colWidth = timelineWidth / numTimeCols;

  // Timeline Header Bar
  doc.setFillColor(30, 41, 59);
  doc.rect(14, ganttStartY, pageWidth - 28, 7.5, 'F');

  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(255, 255, 255);
  doc.text('WBS', 17, ganttStartY + 5);
  doc.text('ACTIVIDAD / PLAN DE TRABAJO', 30, ganttStartY + 5);
  doc.text('RESPONSABLE', 85, ganttStartY + 5);
  doc.text('%', 118, ganttStartY + 5, { align: 'center' });

  // Draw timeline interval headers
  for (let i = 0; i < numTimeCols; i++) {
    const colX = timelineStartX + (i * colWidth);
    const colDate = new Date(minTime + (i * (totalDurationMs / numTimeCols)));
    const dateStr = `${colDate.getDate()} ${colDate.toLocaleDateString('es-MX', { month: 'short' }).toUpperCase()}`;

    doc.setFillColor(i % 2 === 0 ? 47 : 30, i % 2 === 0 ? 60 : 41, i % 2 === 0 ? 82 : 59);
    doc.rect(colX, ganttStartY, colWidth, 7.5, 'F');

    doc.setFontSize(6.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    doc.text(dateStr, colX + (colWidth / 2), ganttStartY + 5, { align: 'center' });
  }

  // Draw Tasks Gantt Rows (show up to 16 tasks on Page 1 visual chart)
  let currentY = ganttStartY + 7.5;
  const rowHeight = 7;
  const tasksToRender = project.tasks.slice(0, 17);

  tasksToRender.forEach((task, idx) => {
    const isEven = idx % 2 === 0;
    
    // Row background
    doc.setFillColor(isEven ? 255 : 248, isEven ? 255 : 250, isEven ? 255 : 252);
    doc.rect(14, currentY, pageWidth - 28, rowHeight, 'F');

    // Left border & grid
    doc.setDrawColor(226, 232, 240);
    doc.line(14, currentY + rowHeight, pageWidth - 14, currentY + rowHeight);

    // Vertical grid lines in timeline area
    for (let i = 0; i <= numTimeCols; i++) {
      const colX = timelineStartX + (i * colWidth);
      doc.setDrawColor(235, 240, 245);
      doc.line(colX, currentY, colX, currentY + rowHeight);
    }

    // Text details
    doc.setFontSize(6.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(71, 85, 105);
    doc.text(task.wbsCode || `${idx + 1}`, 17, currentY + 4.8);

    doc.setFont('helvetica', task.isMilestone ? 'bold' : 'normal');
    doc.setTextColor(task.isMilestone ? 2 : 15, task.isMilestone ? 132 : 23, task.isMilestone ? 199 : 42);
    const taskNameTrunc = task.name.length > 34 ? task.name.substring(0, 34) + '..' : task.name;
    doc.text(task.isMilestone ? `[HITO] ${taskNameTrunc}` : taskNameTrunc, 30, currentY + 4.8);

    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 116, 139);
    const assigneeTrunc = (task.assignee || '-').length > 18 ? (task.assignee || '-').substring(0, 18) + '..' : (task.assignee || '-');
    doc.text(assigneeTrunc, 85, currentY + 4.8);

    // Progress text
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(task.progressPercent === 100 ? 5 : 71, task.progressPercent === 100 ? 150 : 85, task.progressPercent === 100 ? 105 : 105);
    doc.text(`${task.progressPercent}%`, 118, currentY + 4.8, { align: 'center' });

    // Calculate Bar position on timeline
    const tStart = new Date(task.startDate).getTime();
    const tEnd = new Date(task.endDate).getTime();

    if (!isNaN(tStart) && !isNaN(tEnd) && tEnd >= tStart) {
      const startRatio = Math.max(0, Math.min(1, (tStart - minTime) / totalDurationMs));
      const endRatio = Math.max(0, Math.min(1, (tEnd - minTime) / totalDurationMs));
      const durationRatio = Math.max(0.015, endRatio - startRatio);

      const barX = timelineStartX + (startRatio * timelineWidth);
      const barW = Math.max(3, durationRatio * timelineWidth);
      const barY = currentY + 1.8;
      const barH = 3.4;

      if (task.isMilestone) {
        // Milestone Diamond
        doc.setFillColor(2, 132, 199);
        doc.circle(barX + (barW / 2), barY + (barH / 2), 1.8, 'F');
      } else {
        // Background track bar (muted slate)
        doc.setFillColor(226, 232, 240);
        doc.roundedRect(barX, barY, barW, barH, 0.8, 0.8, 'F');

        // Filled Progress Bar
        const fillW = (barW * Math.min(100, Math.max(0, task.progressPercent))) / 100;
        if (fillW > 0) {
          if (task.status === 'COMPLETADA' || task.progressPercent === 100) {
            doc.setFillColor(5, 150, 105); // Green
          } else if (task.status === 'RETRASADA' || task.status === 'BLOQUEADA') {
            doc.setFillColor(220, 38, 38); // Red
          } else if (task.status === 'EN_REVISION') {
            doc.setFillColor(217, 119, 6); // Amber
          } else {
            doc.setFillColor(2, 132, 199); // Blue
          }
          doc.roundedRect(barX, barY, Math.max(1, fillW), barH, 0.8, 0.8, 'F');
        }
      }
    }

    currentY += rowHeight;
  });

  // Note if tasks were truncated on visual Gantt page
  if (project.tasks.length > 17) {
    doc.setFontSize(6.5);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(100, 116, 139);
    doc.text(`* Mostrando las primeras 17 de ${project.tasks.length} actividades en cronograma visual. Desglose completo y entregables en la siguiente página.`, 14, currentY + 4);
  }

  // ==========================================
  // PAGE 2: DESGLOSE DETALLADO DE ACTIVIDADES Y FIRMAS
  // ==========================================
  doc.addPage();
  drawHeader('MATRIZ DE ENTREGABLES Y PLAN DETALLADO');

  doc.setTextColor(15, 23, 42);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text(`Desglose Detallado de Actividades y Matriz de Control: ${project.code}`, 14, 24);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(100, 116, 139);
  doc.text(`Seguimiento WBS, responsables asignados, ventanas operativas y evidencias técnicas de entrega.`, 14, 29);

  // Table Data
  const tableData = project.tasks.map(t => [
    t.wbsCode || '-',
    t.phase || '-',
    t.isMilestone ? `[HITO] ${t.name}` : t.name,
    t.assignee || '-',
    t.startDate || '-',
    t.endDate || '-',
    `${t.durationDays} d`,
    t.status.replace('_', ' '),
    `${t.progressPercent}%`,
    t.deliverable || '-'
  ]);

  autoTable(doc, {
    startY: 33,
    head: [[
      'WBS',
      'Fase',
      'Actividad / Tarea',
      'Responsable',
      'F. Inicio',
      'F. Fin',
      'Duración',
      'Estado',
      '% Avance',
      'Entregable / Evidencia'
    ]],
    body: tableData,
    theme: 'grid',
    headStyles: {
      fillColor: [30, 41, 59],
      textColor: [255, 255, 255],
      fontSize: 7.5,
      fontStyle: 'bold',
      halign: 'center',
      valign: 'middle',
      minCellHeight: 8
    },
    bodyStyles: {
      fontSize: 7,
      textColor: [15, 23, 42],
      valign: 'middle',
      minCellHeight: 6.5
    },
    columnStyles: {
      0: { halign: 'center', cellWidth: 10 },
      1: { cellWidth: 30 },
      2: { cellWidth: 55, fontStyle: 'bold' },
      3: { cellWidth: 30 },
      4: { halign: 'center', cellWidth: 16 },
      5: { halign: 'center', cellWidth: 16 },
      6: { halign: 'center', cellWidth: 13 },
      7: { halign: 'center', cellWidth: 20, fontStyle: 'bold' },
      8: { halign: 'center', cellWidth: 14, fontStyle: 'bold' },
      9: { cellWidth: 65 }
    },
    alternateRowStyles: {
      fillColor: [248, 250, 252]
    },
    didParseCell: (data) => {
      if (data.section === 'body' && data.column.index === 7) {
        const val = String(data.cell.raw);
        if (val.includes('COMPLETADA')) {
          data.cell.styles.textColor = [5, 150, 105];
          data.cell.styles.fillColor = [209, 250, 229];
        } else if (val.includes('EN PROCESO')) {
          data.cell.styles.textColor = [2, 132, 199];
          data.cell.styles.fillColor = [224, 242, 254];
        } else if (val.includes('RETRASADA') || val.includes('BLOQUEADA')) {
          data.cell.styles.textColor = [220, 38, 38];
          data.cell.styles.fillColor = [254, 226, 226];
        }
      }
      if (data.section === 'body' && data.column.index === 8) {
        const pVal = String(data.cell.raw);
        if (pVal === '100%') {
          data.cell.styles.textColor = [5, 150, 105];
        }
      }
    },
    margin: { left: 14, right: 14, bottom: 25 },
    tableWidth: pageWidth - 28
  });

  // --- FOOTER AND SIGNATURES ---
  const finalY = (doc as any).lastAutoTable?.finalY || 140;
  
  if (finalY + 30 <= pageHeight - 15) {
    doc.setDrawColor(148, 163, 184);
    doc.setLineWidth(0.3);

    // Sig 1
    doc.line(35, finalY + 16, 105, finalY + 16);
    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30, 41, 59);
    doc.text(project.manager || 'Líder de Proyecto', 70, finalY + 20, { align: 'center' });
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 116, 139);
    doc.text('Project Manager / Responsable Técnico', 70, finalY + 24, { align: 'center' });

    // Sig 2
    doc.line(pageWidth - 105, finalY + 16, pageWidth - 35, finalY + 16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30, 41, 59);
    doc.text('Dirección de Operaciones / HSEQ', pageWidth - 70, finalY + 20, { align: 'center' });
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 116, 139);
    doc.text(`Grupo ISN - Unidad ${project.businessUnit}`, pageWidth - 70, finalY + 24, { align: 'center' });
  }

  // Running footer on all pages
  const totalPages = (doc.internal as any).getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(148, 163, 184);
    doc.text(
      `Expediente emitido el ${new Date().toLocaleDateString('es-MX')} | SNF - SIC PRO | Página ${i} de ${totalPages}`,
      14,
      pageHeight - 6
    );
    doc.text(
      'Documento de control confidencial interno de Grupo ISN',
      pageWidth - 14,
      pageHeight - 6,
      { align: 'right' }
    );
  }

  doc.save(`GANTT_PLAN_TRABAJO_${project.code}_${project.businessUnit.replace(/\s+/g, '_')}.pdf`);
}

/**
 * Generates an executive summary PDF for all projects in a business unit with visual Gantt progress bars.
 */
export function exportAllProjectsSummaryToPDF(projects: ProjectItem[], businessUnitName: string) {
  const doc = new jsPDF({
    orientation: 'landscape',
    unit: 'mm',
    format: 'a4'
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  // Header
  doc.setFillColor(30, 41, 59);
  doc.rect(0, 0, pageWidth, 16, 'F');
  doc.setFillColor(229, 46, 46);
  doc.rect(0, 16, pageWidth, 2.5, 'F');

  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text('GRUPO ISN  |  SISTEMA INTEGRAL DE CONTROL OPERATIVO - SIC PRO', 14, 11);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.text(`PORTAFOLIO EJECUTIVO & CRONOGRAMA MAESTRO`, pageWidth - 14, 11, { align: 'right' });

  // Title
  doc.setTextColor(15, 23, 42);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.text(`Resumen Ejecutivo de Proyectos y Planes de Trabajo - Sede ${businessUnitName}`, 14, 25);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(100, 116, 139);
  doc.text(`Total de proyectos registrados: ${projects.length}  |  Fecha de reporte: ${new Date().toLocaleDateString('es-MX', { dateStyle: 'full' })}`, 14, 30);

  // Executive KPI summary cards
  const completedProjects = projects.filter(p => p.status === 'COMPLETADO' || p.progressPercent === 100).length;
  const onTrackProjects = projects.filter(p => p.status === 'EN_TIEMPO').length;
  const riskProjects = projects.filter(p => p.status === 'EN_RIESGO' || p.status === 'RETRASADO').length;
  const avgProgress = projects.length > 0 ? Math.round(projects.reduce((acc, p) => acc + (p.progressPercent || 0), 0) / projects.length) : 0;

  const kpis = [
    { title: 'PORTAFOLIO ACTIVO', val: `${projects.length}`, sub: 'Proyectos Registrados', color: [30, 41, 59] },
    { title: 'EN TIEMPO / TERMINADOS', val: `${completedProjects + onTrackProjects}`, sub: `${projects.length > 0 ? Math.round(((completedProjects + onTrackProjects) / projects.length) * 100) : 0}% efectividad`, color: [5, 150, 105] },
    { title: 'EN OBSERVACIÓN / RIESGO', val: `${riskProjects}`, sub: 'Desviaciones operativas', color: riskProjects > 0 ? [220, 38, 38] : [100, 116, 139] },
    { title: 'AVANCE PROMEDIO', val: `${avgProgress}%`, sub: 'Ponderado general', color: [2, 132, 199] }
  ];

  const cardW = (pageWidth - 28 - 9) / 4;
  kpis.forEach((kpi, idx) => {
    const curX = 14 + (idx * (cardW + 3));
    doc.setFillColor(248, 250, 252);
    doc.setDrawColor(226, 232, 240);
    doc.roundedRect(curX, 34, cardW, 18, 1.5, 1.5, 'FD');

    doc.setFontSize(6.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(100, 116, 139);
    doc.text(kpi.title, curX + (cardW / 2), 39, { align: 'center' });

    doc.setFontSize(10.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(kpi.color[0], kpi.color[1], kpi.color[2]);
    doc.text(kpi.val, curX + (cardW / 2), 45.5, { align: 'center' });

    doc.setFontSize(6);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(71, 85, 105);
    doc.text(kpi.sub, curX + (cardW / 2), 49.5, { align: 'center' });
  });

  const tableData = projects.map(p => [
    p.code,
    p.name,
    p.clientName,
    p.category,
    p.manager,
    p.startDate,
    p.targetEndDate,
    p.status.replace('_', ' '),
    p.priority,
    `${p.progressPercent}%`
  ]);

  autoTable(doc, {
    startY: 56,
    head: [[
      'Código',
      'Proyecto',
      'Cliente / Sede',
      'Categoría',
      'Líder PM',
      'Inicio',
      'Compromiso',
      'Estado',
      'Prioridad',
      '% Avance'
    ]],
    body: tableData,
    theme: 'grid',
    headStyles: {
      fillColor: [30, 41, 59],
      textColor: [255, 255, 255],
      fontSize: 7.5,
      fontStyle: 'bold',
      halign: 'center'
    },
    bodyStyles: {
      fontSize: 7,
      textColor: [15, 23, 42]
    },
    columnStyles: {
      0: { fontStyle: 'bold', halign: 'center', cellWidth: 20 },
      1: { fontStyle: 'bold', cellWidth: 50 },
      2: { cellWidth: 35 },
      3: { cellWidth: 25 },
      4: { cellWidth: 30 },
      5: { halign: 'center', cellWidth: 18 },
      6: { halign: 'center', cellWidth: 18 },
      7: { fontStyle: 'bold', halign: 'center', cellWidth: 22 },
      8: { halign: 'center', cellWidth: 18 },
      9: { fontStyle: 'bold', halign: 'center', cellWidth: 18, textColor: [5, 150, 105] }
    },
    alternateRowStyles: {
      fillColor: [248, 250, 252]
    },
    didParseCell: (data) => {
      if (data.section === 'body' && data.column.index === 7) {
        const val = String(data.cell.raw);
        if (val.includes('COMPLETADO') || val.includes('EN TIEMPO')) {
          data.cell.styles.textColor = [5, 150, 105];
          data.cell.styles.fillColor = [209, 250, 229];
        } else if (val.includes('EN RIESGO') || val.includes('RETRASADO')) {
          data.cell.styles.textColor = [220, 38, 38];
          data.cell.styles.fillColor = [254, 226, 226];
        }
      }
    },
    margin: { left: 14, right: 14, bottom: 20 }
  });

  const totalPages = (doc.internal as any).getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(7);
    doc.setTextColor(148, 163, 184);
    doc.text(
      `Reporte Ejecutivo SNF - SIC PRO | Sede ${businessUnitName} | Página ${i} de ${totalPages}`,
      14,
      pageHeight - 6
    );
    doc.text(
      'Documento de control confidencial interno de Grupo ISN',
      pageWidth - 14,
      pageHeight - 6,
      { align: 'right' }
    );
  }

  doc.save(`GANTT_PORTAFOLIO_PROYECTOS_${businessUnitName.replace(/\s+/g, '_')}.pdf`);
}

